import { useContext } from 'react';
import styled, { withTheme } from 'styled-components';
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
  VictoryLine,
  VictoryScatter,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from 'victory';

import { AppContext } from '../../../context/AppContext';
import { min } from '../../../utils/breakpoints';
import { generateTooltipContent } from '../../../utils/socChartUtils';
import Accordion from '../../Accordion';
import { SpacerHr } from '../../Styled';
import StyledTitle from '../../Styled/StyledTitle';

const Container = styled.div`
  margin-top: 24px;
`;

const StyledHighlightInfo = styled.div`
  margin-top: 24px;
  margin-bottom: 24px;
  background-color: ${(props) => `${props.theme.colors.pale}`};
  padding: 14px 22px;
  border-radius: 8px;
  font-style: ${(props) => (props.theme.standardLayout.shouldTextsBeItalic.yourOptimisation ? 'italic' : 'normal')};

  p:last-child {
    margin-bottom: 0;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
  font-size: 14px;
  text-align: left;

  ${min.sm} {
    font-size: 18px;
  }

  th,
  td {
    padding: 12px;
  }

  th {
    font-weight: bold;
    vertical-align: top;
  }

  tr:nth-child(even) {
    background-color: ${(props) => props.theme.colors.extraLight};
  }
`;

const FurtherInfo = ({
  intersectionX,
  intersectionY,
  historicChartData,
  behaviorChartData,
  futureRecords,
  loopIsDisabled,
  theme, // Received from withTheme HOC
}) => {
  const { userDetails, showSimulationMode } = useContext(AppContext);
  const accordionData = [
    {
      title: 'Show list of changes to the battery so far today',
      content: (
        <div>
          <StyledTable>
            <thead>
              <tr>
                <th>Time</th>
                <th className="text-right">Target charge</th>
              </tr>
            </thead>
            <tbody>
              {historicChartData ? (
                historicChartData?.map((row) => (
                  <tr key={`row-${row.x}`}>
                    <td>{row.x}</td>
                    <td className="text-right">{Math.round(row.y)}%</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td>---</td>
                  <td className="text-right">---</td>
                </tr>
              )}
            </tbody>
          </StyledTable>
        </div>
      ),
    },
  ];

  const disabledData = [
    {
      title: 'Show list of changes to the battery so far today',
      content: (
        <div>
          <StyledTable className="mb-0">
            <thead>
              <tr>
                <th>Time</th>
                <th className="text-right">Target charge</th>
              </tr>
            </thead>
          </StyledTable>
          <SpacerHr />
          <div className="text-center font-italic mb-3">{theme.language.appName} is off</div>
        </div>
      ),
    },
  ];

  const placeholderXAxis = Array.from(
    { length: 48 },
    (_, i) => `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 === 0 ? '00' : '30'}`
  );

  const chartAvailable = !!historicChartData?.length && !!behaviorChartData?.length && !!futureRecords?.length;

  const chartTitle =
    showSimulationMode === false && !!userDetails?.simulation_mode
      ? 'Battery state of charge today so far:'
      : 'Your personalised Optimisation plan:';

  // Blacklist elements that shouldn't trigger Voronoi tooltips
  const tooltipBlacklist = ['nowDot', 'nowLine'];

  if (!loopIsDisabled) {
    tooltipBlacklist.push('historicChart');
  }

  return (
    <Container>
      <StyledTitle>{chartTitle}</StyledTitle>
      {
        <VictoryChart
          padding={{ top: 10, bottom: 50, left: 50, right: 14 }}
          containerComponent={
            <VictoryVoronoiContainer
              voronoiBlacklist={tooltipBlacklist}
              labels={({ datum }) => {
                // Use enhanced tooltip for enriched forecast points (future data)
                if (datum.command && datum.startTime) {
                  return generateTooltipContent(datum);
                }
                // Fallback for historic data
                return `Time: ${datum.x}\nState of charge: ${Math.round(datum.y)}%`;
              }}
              labelComponent={
                <VictoryTooltip
                  constrainToVisibleArea={true}
                  cornerRadius={2}
                  pointerLength={4}
                  style={{ fontSize: 11, lineHeight: 1.3 }}
                  flyoutStyle={{
                    fill: theme.colors.white,
                    stroke: 'lightgrey',
                  }}
                  flyoutPadding={{ top: 6, bottom: 6, left: 8, right: 8 }}
                />
              }
            />
          }
        >
          <VictoryAxis
            dependentAxis
            tickValues={[0, 25, 50, 75, 100]}
            label="Battery state of charge (%)"
            style={{
              axis: { stroke: 'transparent' },
              ticks: { stroke: 'transparent' },
              tickLabels: {
                fill: theme.colors.black,
                fontSize: 10,
                padding: 5,
                fontWeight: 'normal',
              },
              axisLabel: {
                fontSize: 12,
                padding: 35,
                angle: -90,
                textAnchor: 'middle',
                fill: theme.colors.black,
                fontWeight: 'normal',
              },
              grid: {
                stroke: behaviorChartData ? theme.colors.grey : 'none',
              },
            }}
          />
          {chartAvailable && !loopIsDisabled && (
            <VictoryLine
              name="futurePlan"
              data={behaviorChartData}
              style={{
                data: {
                  stroke: theme.colors.secondary,
                  strokeDasharray: '5.5,3',
                  strokeWidth: 1.6,
                },
              }}
            />
          )}
          {chartAvailable && (
            <VictoryArea
              name="historicChart"
              data={historicChartData}
              style={{
                data: {
                  fill: theme.colors.secondary,
                  stroke: theme.colors.secondary,
                  strokeWidth: 2,
                  fillOpacity: 0.1,
                },
              }}
            />
          )}
          <VictoryAxis
            tickValues={behaviorChartData ? behaviorChartData?.map((d) => d.x) : placeholderXAxis}
            label="Half-hourly time period"
            tickFormat={(t, index, ticks) => {
              if (index === 0 || index === ticks.length - 1 || t === intersectionX) {
                return t;
              }
              return '';
            }}
            style={{
              ticks: { stroke: theme.colors.light, size: 5 },
              tickLabels: {
                fill: theme.colors.black,
                fontSize: 10,
                padding: 5,
              },
              axis: { stroke: theme.colors.light, strokeWidth: 2 },
              axisLabel: {
                fontSize: 12,
                padding: 30,
                textAnchor: 'middle',
                fill: theme.colors.black,
                fontWeight: 'normal',
              },
            }}
          />
          {chartAvailable && (
            <VictoryLine
              name="nowLine"
              data={[
                { x: intersectionX, y: -5 },
                { x: intersectionX, y: 120 },
              ]}
              style={{
                data: {
                  stroke: theme.colors.black,
                  strokeWidth: 2,
                  fillOpacity: 0.5,
                },
              }}
              labels={() => 'Now'}
              labelComponent={
                <VictoryLabel
                  dy={7}
                  backgroundPadding={{ top: 8, bottom: 6, left: 14, right: 14 }}
                  backgroundStyle={{ fill: theme.colors.black }}
                  style={{
                    fill: theme.colors.white,
                    fontSize: 10,
                    fontWeight: 'bold',
                  }}
                  backgroundComponent={<rect rx="2" ry="2" />}
                />
              }
            />
          )}
          {chartAvailable && (
            <VictoryScatter
              name="nowDot"
              data={[
                {
                  x: intersectionX,
                  y: intersectionY,
                },
              ]}
              size={5}
              style={{
                data: {
                  fill: theme.colors.secondary,
                  stroke: theme.colors.white,
                  strokeWidth: 2,
                },
              }}
            />
          )}
          {!chartAvailable && (
            <>
              <VictoryLabel
                textAnchor="middle"
                style={{ fontSize: 11, fontWeight: 'bold' }}
                x={'50%'}
                y={140}
                text="Not enough data to show personalised Optimisation plan."
              />
              <VictoryLabel
                textAnchor="middle"
                style={{ fontSize: 11 }}
                x={'50%'}
                y={160}
                text="Check back in 30 minutes."
              />
            </>
          )}
        </VictoryChart>
      }
      <p>
        {theme.language.appName}s your electricity bill by charging your battery when electricity is cheap, and using
        this energy when electricity is expensive.
      </p>

      <StyledHighlightInfo>
        <p>
          {theme.language.brandName}'s optimisation runs every half hour and reacts to changes in your energy demand or
          generation.
        </p>
      </StyledHighlightInfo>

      <Accordion data={!loopIsDisabled ? accordionData : disabledData} />
    </Container>
  );
};

export default withTheme(FurtherInfo);
